// ── arcadeJuice — игровой отклик темы «Аркада» ───────────────────────────────
// То, что делает Balatro «сочной»: на каждое действие — ответ глазами и ушами.
//  • любое нажатие (кнопка, ссылка, строка списка) — фирменный звон, светящееся кольцо
//    из точки нажатия и волна по живому столу; объёмная кнопка (data-lift) — вместо звона
//    «щелчок фишки» и искры её цвета;
//  • касание мимо кнопок тоже отвечает, и звук зависит от поверхности: пустой стол —
//    перебор калимбы (нота по месту касания) и волна по столу; стеклянные полосы (шапка,
//    боковое и нижнее меню, мини-плеер: data-surface="glass") — глухой стеклянный «тинь» и
//    звёздочка-блик; панели — мягкий стук. Звучит по отпусканию, если палец не сдвинулся, —
//    прокрутка страницы молчит;
//  • плитки и карта (data-tilt) наклоняются за курсором, по ним скользит блик;
//  • ответ в повторении — нота: «Хорошо»/«Легко» подряд звучат всё выше (серия, как
//    подсчёт очков в игре), «Снова» — глухой удар и тряска карты; над кнопкой всплывают
//    «+1» и «×серия», на каждые 5 подряд — большое «×5!» по центру с искрами;
//    конец подхода — праздничный перезвон и фонтан искр из центра (celebrate).
// Звук синтезируется на месте (Web Audio), файлов нет. Молчит, если в настройках
// выключено («Звуки интерфейса», ac_ui_sounds), если сейчас играет аудио или видео
// или читается текст вслух — урок важнее щелчков. «Меньше анимации» — без искр и наклона.
//
//   startArcadeJuice() / stopArcadeJuice() — App вместе с живым столом (тема с table)
//   answerSound(tone, combo), celebrate()  — повторение
// Это и общий вход для звуков ответа, карт и праздника: в «Колоде» (kit.sounds 'table')
// answerSound/cardSound/celebrate отвечают записями фишек и карт — src/tableSounds.js.

import { PRESSABLE, QUIET, mediaBusy, uiSoundsEnabled } from './soundGate';
import { tableAnswer, tableCard, tableCelebrate, tableSoundsOn } from './tableSounds';

let on = false;
let layer = null;
let actx = null;
let off = null;

const reduced = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ── Искры ───────────────────────────────────────────────────────────────────
const SPARK = ['#fda200', '#fff27a', '#ffffff'];
function sparks(x, y, color, n = 10, power = 1) {
  if (!layer || reduced()) return;
  for (let i = 0; i < n; i++) {
    const s = document.createElement('i');
    const size = 3 + Math.round(Math.random() * 3) * 2;
    s.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${size}px;background:${i % 3 ? color : SPARK[i % SPARK.length]};box-shadow:0 0 6px ${color};`;
    layer.appendChild(s);
    const a = Math.random() * Math.PI * 2;
    const v = (40 + Math.random() * 70) * power;
    const dx = Math.cos(a) * v;
    const dy = Math.sin(a) * v - 30 * power;
    const anim = s.animate([
      { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 60 * power}px)) scale(.3)`, opacity: 0 },
    ], { duration: 420 + Math.random() * 260, easing: 'cubic-bezier(.2,.7,.4,1)' });
    anim.onfinish = () => s.remove();
  }
}

// ── Звук ────────────────────────────────────────────────────────────────────
function ctx() {
  if (!on || !uiSoundsEnabled() || mediaBusy()) return null;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!actx) actx = new AC();
    if (actx.state === 'suspended') actx.resume().catch(() => {});
    return actx;
  } catch { return null; }
}
// Одна нота: частота, длительность, форма волны, громкость, скольжение частоты.
function tone(a, { f, d = 0.12, type = 'square', vol = 0.05, slide = 1, at = 0 }) {
  const t0 = a.currentTime + at;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f, t0);
  if (slide !== 1) o.frequency.exponentialRampToValueAtTime(f * slide, t0 + d);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + d);
  o.connect(g).connect(a.destination);
  o.start(t0);
  o.stop(t0 + d + 0.02);
}
// «Фишка»: короткий высокий щелчок.
function chip() {
  const a = ctx();
  if (!a) return;
  tone(a, { f: 1300, d: 0.05, type: 'triangle', vol: 0.06, slide: 1.4 });
  tone(a, { f: 2600, d: 0.03, type: 'square', vol: 0.015, at: 0.01 });
}
// ── Очки на экране: всплывающие надписи и тряска ─────────────────────────────
let lastX = 0;
let lastY = 0; // где последний раз нажимали — оттуда всплывают «+1»
function popText(text, x, y, color, big = false) {
  if (!layer || reduced()) return;
  const el = document.createElement('b');
  el.textContent = text;
  el.style.cssText = `position:absolute;left:${x}px;top:${y}px;font:700 ${big ? 54 : 22}px var(--ac-font);color:${color};white-space:nowrap;`
    + 'text-shadow:0 0 4px rgba(0,0,0,.8),0 3px 0 rgba(0,0,0,.55);translate:-50% -50%;';
  layer.appendChild(el);
  const rise = big ? 40 : 70;
  const anim = el.animate([
    { transform: 'scale(.4) rotate(-8deg)', opacity: 0 },
    { transform: 'scale(1.25) rotate(4deg)', opacity: 1, offset: 0.25 },
    { transform: 'scale(1) rotate(0deg)', opacity: 1, offset: 0.55 },
    { transform: `translateY(-${rise}px) scale(.9)`, opacity: 0 },
  ], { duration: big ? 1100 : 800, easing: 'ease-out' });
  anim.onfinish = () => el.remove();
}
// Тряска карты: «Снова» — короткая, рекорд серии — сильнее.
function shake(power = 1) {
  if (reduced()) return;
  const d = 3 * power;
  const frames = [
    { transform: 'translate(0,0)' }, { transform: `translate(${-d}px,${d / 2}px) rotate(-1deg)` },
    { transform: `translate(${d}px,${-d / 2}px) rotate(1deg)` }, { transform: `translate(${-d / 2}px,0)` }, { transform: 'translate(0,0)' },
  ];
  // Карта и панель ответов трясутся вместе — как весь стол от удара.
  for (const el of document.querySelectorAll('[data-playcard], .ac-quiz-actions')) el.animate?.(frames, { duration: 260 + 80 * power, easing: 'ease-out' });
}

// Верный ответ: из карты разлетаются масти и покерные фишки, падая, как с подброшенной колоды.
const SUITS = [['♥', '#fe5f55'], ['♦', '#fe5f55'], ['♠', '#f4ecd8'], ['♣', '#f4ecd8']];
const CHIPS = ['#009dff', '#fe5f55', '#fda200', '#4bc292'];
function cardBurst(big = false) {
  const card = document.querySelector('[data-playcard]');
  if (!layer || !card || reduced()) return;
  const r = card.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const n = big ? 16 : 10;
  for (let i = 0; i < n; i++) {
    const el = document.createElement('i');
    if (i % 2) {
      const [s, c] = SUITS[i % SUITS.length];
      el.textContent = s;
      el.style.cssText = `position:absolute;left:${cx}px;top:${cy}px;font:700 ${18 + (i % 3) * 4}px Georgia,serif;color:${c};text-shadow:0 2px 0 rgba(0,0,0,.45);`;
    } else {
      const c = CHIPS[i % CHIPS.length];
      el.style.cssText = `position:absolute;left:${cx}px;top:${cy}px;width:16px;height:16px;border-radius:50%;background:${c};`
        + 'box-shadow:inset 0 0 0 3px rgba(255,255,255,.85),inset 0 0 0 5px ' + c + ',0 2px 0 rgba(0,0,0,.4);';
    }
    layer.appendChild(el);
    const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.3;
    const v = 90 + Math.random() * 110 * (big ? 1.5 : 1);
    const dx = Math.cos(a) * v;
    const dy = Math.sin(a) * v;
    const anim = el.animate([
      { transform: 'translate(-50%,-50%) rotate(0deg) scale(.6)', opacity: 1 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${(Math.random() - 0.5) * 360}deg) scale(1)`, opacity: 1, offset: 0.45 },
      { transform: `translate(calc(-50% + ${dx * 1.4}px), calc(-50% + ${dy + 220}px)) rotate(${(Math.random() - 0.5) * 720}deg) scale(.9)`, opacity: 0 },
    ], { duration: 800 + Math.random() * 300, easing: 'cubic-bezier(.2,.6,.4,1)' });
    anim.onfinish = () => el.remove();
  }
}
// Что показать на экране после ответа: «+1» синим/зелёным у кнопки, «×серия» красным,
// на 5, 10, 15… подряд — большая надпись по центру, искры и тряска.
function answerFx(kind, combo) {
  if (!on) return;
  const x = lastX || window.innerWidth / 2;
  const y = (lastY || window.innerHeight * 0.75) - 30;
  if (kind === 'again') { popText('×0', x, y, '#fe5f55'); shake(1); return; }
  popText('+1', x - 14, y, kind === 'easy' ? '#009dff' : kind === 'hard' ? '#fda200' : '#4bc292');
  if (kind !== 'hard') cardBurst(combo >= 5);
  if (combo >= 2) setTimeout(() => popText(`×${combo}`, x + 22, y - 18, '#fe5f55'), 90);
  if (combo >= 5 && combo % 5 === 0) {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight * 0.36;
    setTimeout(() => {
      popText(`×${combo}!`, cx, cy, '#fda200', true);
      ['#fe5f55', '#009dff', '#fda200'].forEach((c, i) => setTimeout(() => sparks(cx, cy, c, 16, 2), i * 70));
      shake(2);
    }, 150);
  }
}

// Пентатоника до мажор: серия правильных ответов поднимается по ней.
const SCALE = [523.25, 587.33, 659.25, 783.99, 880, 1046.5, 1174.66, 1318.51, 1567.98, 1760];
export function answerSound(kind, combo = 0) {
  if (tableSoundsOn()) { tableAnswer(kind, combo); return; }
  answerFx(kind, combo);
  const a = ctx();
  if (!a) return;
  if (combo >= 5 && combo % 5 === 0) [659.25, 783.99, 1046.5, 1318.51].forEach((f, i) => tone(a, { f, d: 0.16, type: 'square', vol: 0.03, at: 0.15 + i * 0.07 }));
  if (kind === 'again') {
    tone(a, { f: 150, d: 0.22, type: 'sawtooth', vol: 0.05, slide: 0.5 });
    tone(a, { f: 95, d: 0.25, type: 'sine', vol: 0.08, slide: 0.6 });
    return;
  }
  if (kind === 'hard') { tone(a, { f: 440, d: 0.16, type: 'triangle', vol: 0.06 }); return; }
  const f = SCALE[Math.min(SCALE.length - 1, Math.max(0, combo - 1))] * (kind === 'easy' ? 1.25 : 1);
  tone(a, { f, d: 0.14, type: 'square', vol: 0.035 });
  tone(a, { f: f * 2, d: 0.18, type: 'sine', vol: 0.04, at: 0.03 });
}

// Шорох карты: переворот (flip) и раздача новой (deal) — короткий шум с фильтром.
function noise(a, { d = 0.08, freq = 2400, q = 0.8, vol = 0.05, at = 0 }) {
  const n = Math.max(1, Math.floor(a.sampleRate * d));
  const buf = a.createBuffer(1, n, a.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < n; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = a.createBufferSource();
  const f = a.createBiquadFilter();
  const g = a.createGain();
  src.buffer = buf;
  f.type = 'bandpass';
  f.frequency.value = freq;
  f.Q.value = q;
  g.gain.value = vol;
  src.connect(f).connect(g).connect(a.destination);
  src.start(a.currentTime + at);
}
export function cardSound(kind = 'flip') {
  if (tableSoundsOn()) { tableCard(kind); return; }
  const a = ctx();
  if (!a) return;
  if (kind === 'deal') { noise(a, { d: 0.12, freq: 1800, vol: 0.06 }); tone(a, { f: 700, d: 0.05, type: 'triangle', vol: 0.02, slide: 1.3, at: 0.05 }); return; }
  noise(a, { d: 0.06, freq: 3200, vol: 0.05 });
  noise(a, { d: 0.05, freq: 2200, vol: 0.04, at: 0.06 });
}

// Звуки игры «Рой слов». Частые (сбил слово) — не чаще раза в 50 мс, чтобы не трещало.
const lastSfx = {};
export function gameSfx(kind) {
  const now = performance.now();
  if (kind === 'kill' && now - (lastSfx.kill || 0) < 50) return;
  if (kind === 'hurt' && now - (lastSfx.hurt || 0) < 200) return;
  if (kind === 'xp' && now - (lastSfx.xp || 0) < 45) return;
  if ((kind === 'boom' || kind === 'crit') && now - (lastSfx[kind] || 0) < 70) return;
  lastSfx[kind] = now;
  const a = ctx();
  if (!a) return;
  switch (kind) {
    case 'kill': noise(a, { d: 0.05, freq: 1200 + Math.random() * 1600, vol: 0.035 }); break;
    case 'hurt': tone(a, { f: 120, d: 0.18, type: 'sawtooth', vol: 0.05, slide: 0.6 }); break;
    case 'grab': tone(a, { f: 660, d: 0.08, type: 'triangle', vol: 0.05, slide: 1.6 }); break;
    case 'xp': tone(a, { f: 1500 + Math.random() * 400, d: 0.04, type: 'sine', vol: 0.025 }); break;
    case 'target': tone(a, { f: 880, d: 0.1, type: 'triangle', vol: 0.04, slide: 1.5 }); break;
    case 'drop': tone(a, { f: 330, d: 0.08, type: 'triangle', vol: 0.04, slide: 0.7 }); break;
    case 'zap': noise(a, { d: 0.12, freq: 3200, q: 1.2, vol: 0.05 }); tone(a, { f: 900, d: 0.1, type: 'square', vol: 0.02, slide: 0.4 }); break;
    case 'boom': noise(a, { d: 0.22, freq: 220, q: 0.7, vol: 0.09 }); break;
    case 'crit': tone(a, { f: 1760, d: 0.06, type: 'square', vol: 0.025 }); break;
    case 'block': tone(a, { f: 1200, d: 0.15, type: 'triangle', vol: 0.05, slide: 1.5 }); tone(a, { f: 1800, d: 0.12, type: 'sine', vol: 0.03, at: 0.03 }); break;
    case 'far': tone(a, { f: 300, d: 0.08, type: 'triangle', vol: 0.03 }); break;
    case 'deliver': [784, 988, 1319].forEach((f, i) => tone(a, { f, d: 0.14, type: 'square', vol: 0.03, at: i * 0.06 })); break;
    case 'wrong': tone(a, { f: 180, d: 0.25, type: 'square', vol: 0.04, slide: 0.7 }); break;
    case 'shock': noise(a, { d: 0.25, freq: 300, q: 0.5, vol: 0.08 }); break;
    case 'level': [523, 659, 784, 1047, 1319].forEach((f, i) => tone(a, { f, d: 0.18, type: 'square', vol: 0.03, at: i * 0.07 })); break;
    case 'over': [392, 330, 262, 196].forEach((f, i) => tone(a, { f, d: 0.3, type: 'triangle', vol: 0.05, at: i * 0.16 })); break;
    default: break;
  }
}
// Тихий щелчок при наведении на кнопку (только мышь), не чаще раза в 60 мс.
let lastTick = 0;
function onOver(e) {
  if (e.pointerType !== 'mouse') return;
  const b = e.target?.closest?.('[data-lift]');
  if (!b || b.disabled || b.contains(e.relatedTarget)) return;
  const now = performance.now();
  if (now - lastTick < 60) return;
  lastTick = now;
  const a = ctx();
  if (a) tone(a, { f: 2100, d: 0.025, type: 'triangle', vol: 0.012 });
}

// Монеты: золотые кружки сыплются сверху, крутясь, — джекпот.
function coins(n = 36) {
  if (!layer || reduced()) return;
  const W = window.innerWidth;
  const H = window.innerHeight;
  for (let i = 0; i < n; i++) {
    const c = document.createElement('i');
    const s = 14 + Math.round(Math.random() * 10);
    c.style.cssText = `position:absolute;left:${Math.random() * W}px;top:-30px;width:${s}px;height:${s}px;border-radius:50%;`
      + 'background:radial-gradient(circle at 35% 30%,#fff6c2,#ffd24a 38%,#e09400 70%,#a86400);box-shadow:0 0 8px rgba(255,200,60,.8),inset 0 -2px 0 rgba(0,0,0,.25);';
    layer.appendChild(c);
    const dx = (Math.random() - 0.5) * 160;
    const anim = c.animate([
      { transform: 'translate(0,0) rotateY(0deg)', opacity: 1 },
      { transform: `translate(${dx}px, ${H + 60}px) rotateY(${720 + Math.random() * 720}deg)`, opacity: 1 },
    ], { duration: 1400 + Math.random() * 1200, delay: Math.random() * 600, easing: 'cubic-bezier(.3,.1,.7,1)', fill: 'backwards' });
    anim.onfinish = () => c.remove();
  }
}

// Конец подхода: перезвон и фонтан искр из центра экрана. jackpot — выполнен план дня:
// ещё и дождь монет, звон и надпись «ДЖЕКПОТ!».
export function celebrate(jackpot = false, label = 'JACKPOT!') {
  if (tableSoundsOn()) { tableCelebrate(jackpot); return; }
  if (!on) return;
  const a = ctx();
  if (a) [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(a, { f, d: 0.22, type: 'square', vol: 0.035, at: i * 0.09 }));
  const x = window.innerWidth / 2;
  const y = window.innerHeight * 0.32;
  ['#fe5f55', '#009dff', '#fda200', '#4bc292'].forEach((c, i) => setTimeout(() => sparks(x, y, c, 14, 1.8), i * 90));
  if (!jackpot) return;
  coins();
  setTimeout(() => popText(label, x, y, '#ffd24a', true), 250);
  if (a) for (let i = 0; i < 10; i++) tone(a, { f: 1800 + Math.random() * 1400, d: 0.06, type: 'triangle', vol: 0.025, at: 0.35 + i * 0.07 });
}

// ── Наклон за курсором (только мышь: на телефоне карта и так покачивается) ────
let tilted = null;
function untilt() {
  if (!tilted) return;
  tilted.style.removeProperty('--ac-rx');
  tilted.style.removeProperty('--ac-ry');
  tilted.style.removeProperty('--ac-gx');
  tilted.style.removeProperty('--ac-gy');
  tilted.removeAttribute('data-tilting');
  tilted = null;
}
function onMove(e) {
  if (e.pointerType !== 'mouse' || reduced()) return;
  const el = e.target?.closest?.('[data-tilt]');
  if (el !== tilted) untilt();
  if (!el) return;
  const r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width - 0.5;
  const py = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty('--ac-rx', `${(-py * 7).toFixed(2)}deg`);
  el.style.setProperty('--ac-ry', `${(px * 9).toFixed(2)}deg`);
  el.style.setProperty('--ac-gx', `${Math.round((px + 0.5) * 100)}%`);
  el.style.setProperty('--ac-gy', `${Math.round((py + 0.5) * 100)}%`);
  el.setAttribute('data-tilting', '');
  tilted = el;
}

// «Звон» — фирменный звук нажатия: короткий светлый удар, как по монетке.
let lastDing = 0;
function ding() {
  const now = performance.now();
  if (now - lastDing < 45) return;
  lastDing = now;
  const a = ctx();
  if (!a) return;
  tone(a, { f: 1567.98, d: 0.22, type: 'sine', vol: 0.035 });
  tone(a, { f: 3135.96, d: 0.12, type: 'sine', vol: 0.012, at: 0.004 });
}
// Тонкое светящееся кольцо из точки нажатия.
function ringAt(x, y) {
  if (!layer || reduced()) return;
  const r = document.createElement('i');
  r.style.cssText = `position:absolute;left:${x - 22}px;top:${y - 22}px;width:44px;height:44px;border-radius:50%;`
    + 'border:2px solid rgba(255,255,255,.75);box-shadow:0 0 12px rgba(255,255,255,.45);';
  layer.appendChild(r);
  const anim = r.animate([{ transform: 'scale(.2)', opacity: 0.9 }, { transform: 'scale(1.3)', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.2,.7,.4,1)' });
  anim.onfinish = () => r.remove();
}

// ── Касание мимо кнопок: звук и отклик по поверхности ─────────────────────────
// Не отвечаем там, где касание — часть работы (QUIET в soundGate.js) и при выделении текста.
// Поверхность под точкой: ближайший предок с data-surface; иначе первый непрозрачный
// (фон плотнее 0.3 или с картинкой/градиентом) — панель; ничего — пустой стол.
function surfaceOf(el) {
  for (let n = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    if (n.dataset?.surface) return n.dataset.surface;
    const cs = getComputedStyle(n);
    if (cs.backgroundImage && cs.backgroundImage !== 'none') return 'panel';
    const m = cs.backgroundColor.match(/rgba?\(([^)]+)\)/);
    const alpha = m ? Number(m[1].split(',')[3] ?? 1) : 0;
    if (alpha > 0.3) return 'panel';
  }
  return 'table';
}
// Калимба: пентатоника, нота — по горизонтали касания (слева низкие, справа высокие).
const KALIMBA = [523.25, 587.33, 659.25, 783.99, 880, 1046.5, 1174.66];
function surfaceSound(kind, x) {
  const a = ctx();
  if (!a) return;
  if (kind === 'table') {
    const f = KALIMBA[Math.max(0, Math.min(KALIMBA.length - 1, Math.floor((x / window.innerWidth) * KALIMBA.length)))];
    tone(a, { f, d: 0.38, type: 'sine', vol: 0.04 });
    tone(a, { f: f * 2.01, d: 0.12, type: 'sine', vol: 0.012 });
  } else if (kind === 'glass') {
    // стекло, но приглушённое: высокий короткий звон без «хвоста» и щелчок по кромке
    tone(a, { f: 2349.3, d: 0.11, type: 'sine', vol: 0.02 });
    tone(a, { f: 3520, d: 0.05, type: 'sine', vol: 0.008, at: 0.005 });
    noise(a, { d: 0.015, freq: 5200, q: 6, vol: 0.012 });
  } else {
    // панель: мягкий стук, как по карте на сукне
    noise(a, { d: 0.035, freq: 700, q: 0.9, vol: 0.03 });
    tone(a, { f: 196, d: 0.06, type: 'triangle', vol: 0.015 });
  }
}
// Звёздочка-блик на стекле: четыре луча, поворот и тает.
function glint(x, y) {
  if (!layer || reduced()) return;
  const g = document.createElement('i');
  g.style.cssText = `position:absolute;left:${x - 14}px;top:${y - 14}px;width:28px;height:28px;background:#fff;`
    + 'clip-path:polygon(50% 0,57% 43%,100% 50%,57% 57%,50% 100%,43% 57%,0 50%,43% 43%);';
  layer.appendChild(g);
  const anim = g.animate([{ transform: 'scale(.2) rotate(0deg)', opacity: 1 }, { transform: 'scale(1.1) rotate(90deg)', opacity: 0 }], { duration: 420, easing: 'cubic-bezier(.2,.7,.4,1)' });
  anim.onfinish = () => g.remove();
}
let tap = null; // { id, x, y, t, kind }
function onUp(e) {
  const tp = tap;
  tap = null;
  if (!tp || tp.id !== e.pointerId || performance.now() - tp.t > 600 || Math.hypot(e.clientX - tp.x, e.clientY - tp.y) > 10) return;
  if (window.getSelection?.()?.toString()) return;
  surfaceSound(tp.kind, tp.x);
  if (tp.kind === 'table') {
    try { window.dispatchEvent(new CustomEvent('ac:ripple', { detail: { x: tp.x, y: tp.y } })); } catch { /* старый браузер */ }
    ringAt(tp.x, tp.y);
    sparks(tp.x, tp.y, ['#fe5f55', '#009dff', '#fda200'][Math.floor(Math.random() * 3)], 5, 0.7);
  } else if (tp.kind === 'glass') glint(tp.x, tp.y);
  else ringAt(tp.x, tp.y);
}

function onDown(e) {
  lastX = e.clientX;
  lastY = e.clientY;
  const el = e.target?.closest?.(PRESSABLE);
  if (!el) {
    tap = e.target?.closest?.(QUIET) ? null : { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), kind: surfaceOf(e.target) };
    return;
  }
  tap = null;
  if (el.disabled) return;
  // Любое нажатие: звон, кольцо и волна по столу (arcadeTable). Объёмная кнопка — ещё и
  // «фишка» с искрами её цвета.
  try { window.dispatchEvent(new CustomEvent('ac:ripple', { detail: { x: e.clientX, y: e.clientY } })); } catch { /* старый браузер */ }
  ringAt(e.clientX, e.clientY);
  if (el.closest('[data-lift]')) {
    chip();
    const bg = getComputedStyle(el.closest('[data-lift]')).backgroundColor || '#009dff';
    sparks(e.clientX, e.clientY, bg, 9, 1);
  } else ding();
}

export function startArcadeJuice() {
  if (on || typeof document === 'undefined') return;
  on = true;
  layer = document.createElement('div');
  layer.id = 'ac-juice';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147482000;overflow:hidden;';
  document.body.appendChild(layer);
  const leave = () => untilt();
  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('pointerup', onUp, true);
  document.addEventListener('pointermove', onMove, { passive: true });
  document.addEventListener('pointerover', onOver, { passive: true });
  document.addEventListener('pointerleave', leave);
  window.addEventListener('blur', leave);
  off = () => {
    document.removeEventListener('pointerdown', onDown, true);
    document.removeEventListener('pointerup', onUp, true);
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerover', onOver);
    document.removeEventListener('pointerleave', leave);
    window.removeEventListener('blur', leave);
  };
}

export function stopArcadeJuice() {
  if (!on) return;
  on = false;
  off?.();
  untilt();
  layer?.remove();
  layer = null;
}
